import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

export interface MenuItem {
  id?: string;
  name: string;
  desc: string;
  tags: string[];
  allowance?: number;
  event?: string;
  photo_url?: string;
  servings?: number;
  ingredients?: string;
  dietary_flags?: string[];
  caterers?: string[];
  quote_received?: boolean;
  menu_uploaded?: boolean;
  order_confirmed?: boolean;
  invoice_pending?: boolean;
  archived?: boolean;
}

export interface MenuCategory {
  tab: string;
  title: string;
  items: MenuItem[];
  vendor?: 'food' | 'bar';
}

// ── Seed data shown until API loads ───────────────────────────────────────────
const SEED_CATEGORIES: MenuCategory[] = [
  // Traditional
  { tab: 'Traditional', title: 'Starters', items: [
    { name: 'Pepper Soup', desc: 'Assorted meat & fish in spiced broth', tags: ['spicy', 'gluten-free'], allowance: 1500 },
    { name: 'Small Chops', desc: 'Puff puff, spring rolls, samosas, peppered snails', tags: ['finger food'], allowance: 2000 },
  ]},
  { tab: 'Traditional', title: 'Main Course', items: [
    { name: 'Jollof Rice + Chicken', desc: 'Party jollof with smoky base, served with whole chicken leg', tags: ['crowd-favourite'], allowance: 3500 },
    { name: 'Egusi Soup + Pounded Yam', desc: 'Ground melon seed soup with assorted meats', tags: ['Igbo'], allowance: 4000 },
    { name: 'Oha Soup + Eba', desc: 'Seasonal ora leaves with stockfish and crayfish', tags: ['Igbo', 'traditional'], allowance: 3500 },
    { name: 'Banga Soup + Starch', desc: 'Palm nut extract soup with fresh fish', tags: ['Delta', 'traditional'], allowance: 3800 },
    { name: 'Afang Soup + Fufu', desc: 'Wild spinach and water leaves soup', tags: ['Cross-River', 'traditional'], allowance: 3500 },
  ]},
  { tab: 'Traditional', title: 'Drinks', vendor: 'bar', items: [
    { name: 'Zobo', desc: 'Hibiscus flower drink — chilled, no sugar option', tags: ['non-alcoholic'] },
    { name: 'Palm Wine', desc: 'Fresh tapped for the occasion', tags: ['traditional'] },
    { name: 'Malt + Soft Drinks', desc: 'Assorted chilled canned drinks', tags: ['non-alcoholic'] },
  ]},
  // Reception (VIP)
  { tab: 'Reception (VIP)', title: 'Welcome Bites', items: [
    { name: 'Prawn Cocktail', desc: 'Tiger prawns with Marie Rose sauce', tags: ['premium', 'seafood'], allowance: 5000 },
    { name: 'Bruschetta Trio', desc: 'Tomato basil, mushroom, and smoked salmon on toasted ciabatta', tags: ['vegetarian-option'], allowance: 3000 },
  ]},
  { tab: 'Reception (VIP)', title: 'Mains', items: [
    { name: 'Pan-seared Sea Bass', desc: 'With lemon beurre blanc and asparagus tips', tags: ['premium', 'seafood'], allowance: 12000 },
    { name: 'Beef Tenderloin', desc: '180g fillet with truffle jus, mashed potato, and green beans', tags: ['premium'], allowance: 15000 },
    { name: 'Vegetarian Risotto', desc: 'Wild mushroom and parmesan, finished with truffle oil', tags: ['vegetarian'], allowance: 8000 },
  ]},
  { tab: 'Reception (VIP)', title: 'Desserts', items: [
    { name: 'Malva Pudding', desc: 'Warm South African sponge with custard cream', tags: ['dessert'], allowance: 2500 },
    { name: 'Ice Cream Station', desc: 'Three flavours with toppings bar', tags: ['dessert', 'interactive'], allowance: 3000 },
    { name: 'Wedding Cake Slice', desc: 'Presented at VVIP tables', tags: ['dessert'] },
  ]},
  // Reception (Regular)
  { tab: 'Reception (Regular)', title: 'Mains', items: [
    { name: 'Fried Rice + Chicken', desc: 'Nigerian-style fried rice with whole leg', tags: ['crowd-favourite'], allowance: 3000 },
    { name: 'Pepper Rice + Turkey', desc: 'Spiced tomato base with turkey drumstick', tags: [], allowance: 3200 },
    { name: 'Vegetable Soup + Fufu', desc: 'Mixed leafy greens with assorted protein', tags: ['gluten-free'], allowance: 2800 },
  ]},
  { tab: 'Reception (Regular)', title: 'Drinks', vendor: 'bar', items: [
    { name: 'Chapman', desc: 'Bitters, grenadine, Fanta Orange — crowd favourite', tags: ['non-alcoholic'], allowance: 800 },
    { name: 'Soft Drinks + Water', desc: 'Assorted brands, chilled', tags: ['non-alcoholic'] },
  ]},
  // After Party
  { tab: 'After Party', title: 'Small Chops', items: [
    { name: 'Puff Puff', desc: 'Fried dough balls — plain and suya-spiced', tags: ['Nigerian'], allowance: 800 },
    { name: 'Spring Rolls', desc: 'Vegetable and chicken filling, sweet chilli dip', tags: [], allowance: 1200 },
    { name: 'Mini Quiche', desc: 'Spinach & feta, and bacon & cheddar', tags: ['western'], allowance: 1500 },
  ]},
  { tab: 'After Party', title: 'Snacks', items: [
    { name: 'Mini Sliders', desc: 'Beef patty or grilled chicken, brioche bun', tags: ['western'], allowance: 2500 },
    { name: 'Suya Skewers', desc: 'Spiced beef on skewers with sliced onion and tomato', tags: ['Nigerian', 'spicy'], allowance: 2000 },
    { name: 'Cheese Board', desc: 'Selection of cheeses, grapes, crackers, honey', tags: ['premium'], allowance: 4000 },
  ]},
  // After Party drinks (moved from Cocktail Corner)
  { tab: 'After Party', title: 'Signature Drinks', vendor: 'bar', items: [
    { name: 'Chapman', desc: 'House recipe — Campari, Fanta Orange, cucumber, mint', tags: ['non-alcoholic'], allowance: 1200 },
    { name: 'Zobo Bellini', desc: 'Hibiscus reduction with prosecco', tags: ['signature', 'alcoholic'], allowance: 2000 },
    { name: 'Hibiscus Punch', desc: 'Fresh hibiscus, pineapple, ginger — batch-served', tags: ['non-alcoholic'], allowance: 1000 },
  ]},
  { tab: 'After Party', title: 'Mocktails', vendor: 'bar', items: [
    { name: 'Virgin Mojito', desc: 'Lime, mint, soda, sugar — refreshing and clean', tags: ['non-alcoholic'], allowance: 1200 },
    { name: 'Pineapple Mint Cooler', desc: 'Fresh pineapple juice, mint, ginger beer', tags: ['non-alcoholic'], allowance: 1000 },
  ]},
  { tab: 'After Party', title: 'Bar Selections', vendor: 'bar', items: [
    { name: 'Champagne Tower', desc: 'For toasting — on display at reception', tags: ['premium', 'alcoholic'] },
    { name: 'Beer Selection', desc: 'Heineken, Guinness, Star — chilled bucket service', tags: ['alcoholic'] },
  ]},
];

// ── In-memory cache (no localStorage for app data) ──
let menuCache: MenuCategory[] = [];

// ── Global ID assignment for menu items ───
function assignMenuItemIds(categories: MenuCategory[]): MenuCategory[] {
  let itemCounter = 1;
  return categories.map(cat => ({
    ...cat,
    items: cat.items.map(item => ({
      ...item,
      id: item.id || `MI-${String(itemCounter++).padStart(3, '0')}`,
    })),
  }));
}

// ── CRUD functions ─────────────────────────────────────────────────────────

export function loadMenu(): MenuCategory[] {
  const categories = menuCache.length > 0 ? menuCache : SEED_CATEGORIES;
  return assignMenuItemIds(categories);
}

export function saveMenu(categories: MenuCategory[]): void {
  menuCache = categories;
}

export async function addMenuItem(tab: string, title: string, item: MenuItem): Promise<MenuCategory[]> {
  // The server owns the id, and every /menu/:id route parses it with Number().
  // Without adopting the created row's id the item keeps the local `MI-00n`
  // placeholder, so archiving or restoring it later 400s on an invalid ID.
  const created = await apiPost<{ id: number | string }>(planningPath('/menu'), {
    tab, title, name: item.name, desc: item.desc, tags: item.tags, allowance: item.allowance,
  });
  const saved: MenuItem = { ...item, id: String(created.id) };

  const categories = loadMenu();
  const cat = categories.find(c => c.tab === tab && c.title === title);
  if (cat) {
    cat.items.push(saved);
  } else {
    categories.push({ tab, title, items: [saved] });
  }
  saveMenu(categories);
  return categories;
}

export function updateMenuItem(tab: string, title: string, index: number, item: MenuItem): MenuCategory[] {
  const categories = loadMenu();
  const cat = categories.find(c => c.tab === tab && c.title === title);
  if (cat && cat.items[index]) {
    cat.items[index] = item;
  }
  saveMenu(categories);
  return categories;
}

export function deleteMenuItem(tab: string, title: string, index: number): MenuCategory[] {
  const categories = loadMenu();
  const cat = categories.find(c => c.tab === tab && c.title === title);
  if (cat) {
    cat.items.splice(index, 1);
  }
  saveMenu(categories);
  return categories;
}

export interface MenuItemLocation {
  tab: string;
  section: string;
  index: number;
  item: MenuItem;
}

export function findMenuItemById(itemId: string): MenuItemLocation | null {
  const categories = loadMenu();
  for (const cat of categories) {
    for (let i = 0; i < cat.items.length; i++) {
      if (cat.items[i].id === itemId) {
        return {
          tab: cat.tab,
          section: cat.title,
          index: i,
          item: cat.items[i],
        };
      }
    }
  }
  return null;
}

/**
 * Soft-delete: mark as archived. The backend's DELETE /menu/:id route already
 * archives instead of hard-deleting, so this reuses that route rather than
 * sending a separate {archived: true} PUT (that field isn't in the shared
 * Zod schema for this route the same way it is on other entities).
 */
export async function archiveMenuItem(id: string): Promise<MenuCategory[]> {
  const loc = findMenuItemById(id);
  if (!loc) throw new Error('That menu item is no longer listed — refresh and try again.');

  await apiDelete(planningPath(`/menu/${id}`));

  const categories = loadMenu();
  const cat = categories.find(c => c.tab === loc.tab && c.title === loc.section);
  if (cat && cat.items[loc.index]) {
    cat.items[loc.index] = { ...cat.items[loc.index], archived: true };
  }
  saveMenu(categories);
  return categories;
}

/** Restore an archived menu item via the dedicated restore route. */
export async function restoreMenuItem(id: string): Promise<MenuCategory[]> {
  const loc = findMenuItemById(id);
  if (!loc) throw new Error('That menu item is no longer listed — refresh and try again.');

  await apiPut(planningPath(`/menu/${id}/restore`), {});

  const categories = loadMenu();
  const cat = categories.find(c => c.tab === loc.tab && c.title === loc.section);
  if (cat && cat.items[loc.index]) {
    cat.items[loc.index] = { ...cat.items[loc.index], archived: false };
  }
  saveMenu(categories);
  return categories;
}

export interface CateringVendor {
  id: string;
  name: string;
  category: string;
}

/** Vendor categories that count as catering, lower-cased for comparison.
 *  Matched whole rather than by substring so "Fragrance Bar" isn't swept in
 *  alongside "Bar / Cocktails". */
const CATERING_CATEGORIES = new Set([
  'catering',
  'drinks & beverages',
  'bar / cocktails',
  'food service',
  'dessert / cake',
]);

export async function getCateringVendors(): Promise<CateringVendor[]> {
  try {
    // The endpoint returns { vendors: [...] }, not a bare array. Typed as an
    // array, `.filter` threw on the object, the catch swallowed it, and this
    // returned [] every time — the caterer picker was permanently empty.
    const response = await apiGet<{ vendors: Array<{ id: string; name: string; category?: string }> }>(
      planningPath('/vendors')
    );
    return (response.vendors || [])
      // Was an exact, case-sensitive match against a hardcoded list whose
      // names mostly don't exist in the data ('Food Service', 'Drinks / Bar').
      // Only 'Catering' ever matched, and not the lower-cased 'catering' rows.
      .filter(v => !!v.category && CATERING_CATEGORIES.has(v.category.trim().toLowerCase()))
      .map(v => ({ id: v.id, name: v.name, category: v.category || '' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
}

export const MENU_TABS = ['Traditional', 'Reception (VIP)', 'Reception (Regular)', 'After Party'];

/** Maps each menu tab to the caterers/vendors responsible for it */
export const MENU_TAB_CATERERS: Record<string, string[]> = {
  'Traditional':         ['Small Mummy', 'Madam Oge', 'Chef Eros', "Mummy's Caterer"],
  'Reception (VIP)':     ['Chef Eros', 'Madam Oge'],
  'Reception (Regular)': ['Small Mummy', "Mummy's Caterer"],
  'After Party':         ['Hans & Renee', 'Chef Eros'],
};

/** Fetch menu from API, convert flat rows to grouped MenuCategory[], cache in localStorage */
export async function refreshMenuFromApi(): Promise<MenuCategory[]> {
  try {
    const rows = await apiGet<Array<{ id: number | string; tab: string; title: string; name: string; description: string; tags: string[]; allowance: number | null }>>(planningPath('/menu'));
    // Must still go through assignMenuItemIds — returning SEED_CATEGORIES raw
    // left every demo item with no `id` at all, so openEditItem's `item.id ? edit : new`
    // check always took the `new` branch: clicking any item silently opened a blank
    // "New Menu Item" form instead of editing it, on any project with no real menu rows yet.
    if (!Array.isArray(rows) || rows.length === 0) return assignMenuItemIds(SEED_CATEGORIES);

    // Group flat rows into categories
    const catMap = new Map<string, MenuCategory>();
    for (const row of rows) {
      const key = `${row.tab}|${row.title}`;
      if (!catMap.has(key)) {
        catMap.set(key, { tab: row.tab, title: row.title, items: [] });
      }
      catMap.get(key)!.items.push({
        // Carry the real row id. It used to be dropped here, so every item fell
        // through to assignMenuItemIds' positional `MI-00n` placeholder — which
        // /menu/:id parses with Number() and rejects as an invalid ID. Archive
        // and restore were 400ing on every real item; the empty .catch() is the
        // only reason nobody saw it.
        id: String(row.id),
        name: row.name,
        desc: row.description || '',
        tags: row.tags || [],
        allowance: row.allowance ?? undefined,
        photo_url: (row as any).photo_url ?? undefined,
        servings: (row as any).servings ?? undefined,
        ingredients: (row as any).ingredients ?? undefined,
        dietary_flags: (row as any).dietary_flags ?? undefined,
      });
    }
    const categories = Array.from(catMap.values());
    const categoriesWithIds = assignMenuItemIds(categories);
    saveMenu(categoriesWithIds);
    return categoriesWithIds;
  } catch {
    return loadMenu();
  }
}
